import { MaybeDrafted } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import { ResultApiResponse } from 'lib-shared-types-api';
import { createPatch } from 'rfc6902';
import { DeepWritable } from 'ts-essentials';

import { CustomFieldEntity } from 'apis/customFields/types/customFields.types';
import { api } from 'app/app.api';
import { ConversationCustomFieldsUpdatedPayload, pubSub } from 'utils/pubSub';

import {
  CreateAttributeApiArg,
  CreateAttributeApiResponse,
  ReadAttributesApiArg,
  ReadAttributesApiResponse,
  ReadValueApiArg,
  ReadValueApiResponse,
  UpdateAttributeApiArg,
  UpdateAttributeApiResponse,
  UpdateAttributeArchiveStateApiArg,
  UpdateValueApiArg,
  UpdateValueApiResponse,
} from './types/customFieldsApi.types';
import {
  transformCreateAttributeArg,
  transformSelectAttributeOptionsArg,
  transformUpdateValueArg,
} from './utils/transformArg';
import {
  transformCreateAttributeResponse,
  transformReadAttributesResponse,
  transformUpdateAttributeResponse,
} from './utils/transformResponse';

export const customFieldsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    readAttributes: builder.query<
      ReadAttributesApiResponse,
      ReadAttributesApiArg
    >({
      query: ({ entityId }) => ({
        version: 1,
        path: `custom-fields/schema/${entityId}`,
      }),
      transformResponse: transformReadAttributesResponse,
    }),
    createAttribute: builder.mutation<
      CreateAttributeApiResponse,
      CreateAttributeApiArg
    >({
      query: ({ entityId, attribute }) => ({
        version: 1,
        path: `custom-fields/schema/${entityId}`,
        method: 'POST',
        body: transformCreateAttributeArg(attribute),
      }),
      transformResponse: transformCreateAttributeResponse,
      async onQueryStarted({ entityId }, { dispatch, queryFulfilled }) {
        const response = await queryFulfilled;

        dispatch(
          customFieldsApi.util.updateQueryData(
            'readAttributes',
            { entityId },
            (draft) => {
              const attributes = draft.data.attributes as DeepWritable<
                typeof draft.data.attributes
              >;
              attributes.push(
                response.data.data as DeepWritable<typeof response.data.data>,
              );
            },
          ),
        );
      },
    }),
    updateAttribute: builder.mutation<
      UpdateAttributeApiResponse,
      UpdateAttributeApiArg
    >({
      query: ({ entityId, attributeId, patch: { before, after } }) => ({
        version: 1,
        path: `custom-fields/schema/${entityId}/${attributeId}`,
        method: 'PATCH',
        body: createPatch(
          Object.assign(
            {},
            before,
            before.type === 'select'
              ? {
                  options: transformSelectAttributeOptionsArg(before.options),
                }
              : {},
          ),
          Object.assign(
            {},
            after,
            after.type === 'select'
              ? { options: transformSelectAttributeOptionsArg(after.options) }
              : {},
          ),
        ),
      }),
      transformResponse: transformUpdateAttributeResponse,
      async onQueryStarted(
        { entityId, attributeId, patch: { after } },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'readAttributes',
            { entityId },
            (draft) => {
              const attributes = draft.data.attributes as DeepWritable<
                typeof draft.data.attributes
              >;
              const attributeIndex = draft.data.attributes.findIndex(
                ({ id }) => id === attributeId,
              );

              if (attributeIndex !== -1) {
                attributes[attributeIndex] = Object.assign(
                  {},
                  attributes[attributeIndex],
                  after,
                );
              }

              attributes.sort((a, b) => a.order_index - b.order_index);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateAttributeArchiveState: builder.mutation<
      ResultApiResponse,
      UpdateAttributeArchiveStateApiArg
    >({
      query: ({ entityId, attributeId, isArchived }) => ({
        version: 1,
        path: `custom-fields/schema/${entityId}/${attributeId}/archive`,
        method: isArchived ? 'POST' : 'DELETE',
      }),
      async onQueryStarted(
        { entityId, attributeId, isArchived },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'readAttributes',
            { entityId },
            (draft) => {
              const attributes = draft.data.attributes as DeepWritable<
                typeof draft.data.attributes
              >;
              const attributeIndex = draft.data.attributes.findIndex(
                ({ id }) => id === attributeId,
              );

              if (attributeIndex !== -1) {
                attributes[attributeIndex].is_archived = isArchived;
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    readValue: builder.query<ReadValueApiResponse, ReadValueApiArg>({
      query: ({ entityId, entityInstanceId }) => ({
        version: 1,
        path: `custom-fields/data/${entityId}/${entityInstanceId}`,
      }),
      onCacheEntryAdded: async (
        arg,
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) => {
        const entityId: CustomFieldEntity = 'conversation';
        if (arg.entityId !== entityId) {
          return;
        }

        const csid = parseInt(arg.entityInstanceId);

        await cacheDataLoaded;

        const handleConversationCustomFieldsUpdated = (
          payload: ConversationCustomFieldsUpdatedPayload,
        ) => {
          if (payload.csid !== csid) return;

          dispatch(
            customFieldsApi.util.updateQueryData(
              'readValue',
              { entityId, entityInstanceId: csid.toString() },
              (draft) => {
                const mutableDraft = draft as MaybeDrafted<
                  DeepWritable<typeof draft>
                >;

                mutableDraft.data.attribute_values = payload.values;
              },
            ),
          );
        };

        const unsubscribe = pubSub.subscribe(
          'conversationCustomFieldsUpdated',
          handleConversationCustomFieldsUpdated,
        );

        // When the cache entry is removed, clean up the subscription
        await cacheEntryRemoved;
        unsubscribe();
      },
    }),
    updateValue: builder.mutation<UpdateValueApiResponse, UpdateValueApiArg>({
      query: ({ entityId, entityInstanceId, patch }) => ({
        version: 1,
        path: `custom-fields/data/${entityId}/${entityInstanceId}`,
        method: 'PATCH',
        body: transformUpdateValueArg(patch),
      }),
      async onQueryStarted(
        { entityId, entityInstanceId, patch: { after, before } },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          customFieldsApi.util.updateQueryData(
            'readValue',
            { entityId, entityInstanceId },
            (draft) => {
              if (draft?.data) {
                const changedValuesKeys = [
                  ...Object.keys(before),
                  ...Object.keys(after),
                ];

                changedValuesKeys.forEach((key) => {
                  draft.data.attribute_values[key] = after[key];
                });
              }
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});
